import React, { useState, useRef, useEffect } from "react";
import { Input, Tooltip, Select, DatePicker } from "antd";
import {
  RobotOutlined, SendOutlined, AudioOutlined, DeleteOutlined,
  CarOutlined, EnvironmentOutlined, AlertOutlined, CheckCircleOutlined,
  CheckOutlined, CloseOutlined, ArrowRightOutlined, FileTextOutlined,
  ThunderboltOutlined, PlusCircleOutlined, DashboardOutlined, AimOutlined,
  UserOutlined, CalendarOutlined, SwapOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { CALL_API } from "../../Common/api";
import styled from "styled-components";
import { chargetype, priorityType } from "../../Common/constant";
import dayjs from "dayjs";

// ─── Styled ────────────────────────────────────────────────────────────────
const PageWrap = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 70px);
  background: #f3f7f4;
  border-radius: 12px;
  overflow: hidden;

  .chat-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 24px;
    background: linear-gradient(135deg, #061C06 0%, #1a4d1e 55%, #2C963D 100%);
    flex-shrink: 0;
  }
  .chat-header-left { display: flex; align-items: center; gap: 14px; }
  .chat-avatar {
    width: 44px; height: 44px; border-radius: 50%;
    background: rgba(255,255,255,0.15); border: 2px solid rgba(255,255,255,0.35);
    display: flex; align-items: center; justify-content: center;
    font-size: 22px; color: #fff; animation: glow 3s ease-in-out infinite;
  }
  @keyframes glow {
    0%,100%{box-shadow:0 0 0 0 rgba(255,255,255,0.25)}
    50%{box-shadow:0 0 0 8px rgba(255,255,255,0)}
  }
  .chat-header-title { font-size: 17px; font-weight: 700; color: #fff; }
  .chat-header-sub {
    font-size: 12px; color: rgba(255,255,255,0.7);
    display: flex; align-items: center; gap: 5px; margin-top: 2px;
  }
  .live-dot {
    width: 7px; height: 7px; border-radius: 50%; background: #6eff7a;
    animation: blink 1.5s infinite;
  }
  @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.3} }

  /* Progress bar */
  .flow-progress-bar {
    display: flex; gap: 4px; padding: 8px 24px;
    background: rgba(0,0,0,0.25); flex-shrink: 0;
  }
  .flow-progress-seg {
    flex: 1; height: 3px; border-radius: 2px; background: rgba(255,255,255,0.2);
    transition: background 0.4s;
  }
  .flow-progress-seg.done { background: #6eff7a; }

  .chat-body { display: flex; flex: 1; overflow: hidden; }

  /* ── Left sidebar ── */
  .chat-left {
    width: 280px; flex-shrink: 0; background: #fff;
    border-right: 1px solid #e8f5ea;
    display: flex; flex-direction: column; overflow-y: auto;
    padding: 20px 16px; gap: 20px;
  }
  .chat-left::-webkit-scrollbar { width: 4px; }
  .chat-left::-webkit-scrollbar-thumb { background: #c8e6cc; border-radius: 4px; }
  .left-section-title {
    font-size: 11px; font-weight: 700; color: #aaa;
    text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 8px;
  }
  .stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .stat-card {
    background: #f7fdf8; border: 1px solid #e0f0e2; border-radius: 10px;
    padding: 10px; display: flex; flex-direction: column; align-items: center; gap: 3px;
  }
  .stat-icon { font-size: 16px; }
  .stat-val  { font-size: 20px; font-weight: 700; line-height: 1; }
  .stat-lbl  { font-size: 10px; color: #888; text-align: center; }

  .cap-card {
    display: flex; align-items: center; gap: 10px; padding: 10px 12px;
    border-radius: 10px; border: 1px solid #eee; cursor: pointer;
    transition: all 0.18s; background: #fff; margin-bottom: 6px;
  }
  .cap-card:hover { border-color: #2C963D; background: #f7fdf8; transform: translateX(3px); }
  .cap-icon {
    width: 34px; height: 34px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center; font-size: 16px; flex-shrink: 0;
  }
  .cap-label { font-size: 13px; font-weight: 500; color: #1b1b1b; }
  .cap-sub   { font-size: 11px; color: #999; }

  /* ── Right chat ── */
  .chat-right { flex: 1; display: flex; flex-direction: column; overflow: hidden; }

  .messages {
    flex: 1; overflow-y: auto; padding: 24px 28px;
    display: flex; flex-direction: column; gap: 14px;
  }
  .messages::-webkit-scrollbar { width: 5px; }
  .messages::-webkit-scrollbar-thumb { background: #c8e6cc; border-radius: 4px; }

  .msg-row       { display: flex; align-items: flex-end; gap: 10px; }
  .msg-row.user  { flex-direction: row-reverse; }

  .msg-avatar {
    width: 32px; height: 32px; border-radius: 50%;
    background: linear-gradient(135deg, #2C963D, #1a6b28); color: #fff;
    font-size: 14px; display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; box-shadow: 0 2px 8px rgba(44,150,61,0.3);
  }
  .msg-wrap { display: flex; flex-direction: column; max-width: 72%; }
  .msg-row.user .msg-wrap { align-items: flex-end; }

  .msg-bubble {
    padding: 12px 16px; font-size: 14px; line-height: 1.6;
    word-break: break-word; animation: pop-in 0.2s ease;
  }
  @keyframes pop-in { from{transform:scale(0.92);opacity:0} to{transform:scale(1);opacity:1} }

  .msg-bubble.bot {
    background: #fff; color: #1b1b1b;
    border-radius: 4px 18px 18px 18px; box-shadow: 0 2px 8px rgba(0,0,0,0.07);
  }
  .msg-bubble.user {
    background: linear-gradient(135deg, #2C963D, #1a6b28); color: #fff;
    border-radius: 18px 18px 4px 18px;
  }
  .msg-time { font-size: 10px; color: #bbb; margin-top: 4px; padding: 0 4px; }

  .bullet-line { display: flex; gap: 7px; margin: 2px 0; }
  .bullet-dot  { color: #2C963D; font-weight: bold; flex-shrink: 0; }

  /* ── Form widget (inside bot message) ── */
  .form-widget-wrap { margin-top: 12px; }
  .form-widget-box {
    background: #f4fdf6; border: 1px solid #c8e6cc; border-radius: 10px; padding: 12px;
  }
  .widget-label {
    font-size: 10px; font-weight: 700; color: #2C963D;
    text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 8px;
  }

  /* button group */
  .btn-grp { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
  .opt-btn {
    padding: 6px 18px; border-radius: 20px; border: 1.5px solid #c8e6cc;
    background: #fff; color: #555; font-size: 13px; cursor: pointer;
    transition: all 0.18s; outline: none;
  }
  .opt-btn:hover  { border-color: #2C963D; color: #2C963D; }
  .opt-btn.chosen { background: #2C963D; color: #fff; border-color: #2C963D; }

  .widget-next-btn {
    background: linear-gradient(135deg, #2C963D, #1a6b28); color: #fff; border: none;
    border-radius: 20px; padding: 7px 20px; font-size: 13px; font-weight: 600;
    cursor: pointer; transition: all 0.18s; outline: none;
    box-shadow: 0 2px 8px rgba(44,150,61,0.25);
  }
  .widget-next-btn:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  .widget-next-btn:disabled { opacity: 0.38; cursor: not-allowed; transform: none; }
  .widget-skip-btn {
    background: #f0f0f0; color: #888; border: none; border-radius: 20px;
    padding: 7px 16px; font-size: 13px; cursor: pointer; outline: none;
    transition: all 0.18s;
  }
  .widget-skip-btn:hover { background: #e0e0e0; }

  /* completed step display */
  .step-done {
    margin-top: 8px; padding: 6px 10px; background: #edf9ef;
    border-radius: 20px; font-size: 12px; color: #1a6b28;
    display: inline-flex; align-items: center; gap: 5px;
  }

  /* action / result buttons */
  .action-wrap { padding: 7px 0 4px 42px; }
  .action-btn {
    display: inline-flex; align-items: center; gap: 6px;
    background: linear-gradient(135deg, #2C963D, #1a6b28); color: #fff;
    border: none; border-radius: 20px; padding: 7px 18px; font-size: 13px;
    font-weight: 600; cursor: pointer; transition: transform 0.2s, box-shadow 0.2s;
    outline: none; box-shadow: 0 2px 10px rgba(44,150,61,0.3);
  }
  .action-btn:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(44,150,61,0.4); }

  /* ── Preview card ── */
  .preview-card {
    background: #fff; border: 1.5px solid #2C963D; border-radius: 14px;
    overflow: hidden; margin-top: 10px; animation: pop-in 0.25s ease;
  }
  .preview-header {
    background: linear-gradient(135deg, #061C06, #2C963D);
    padding: 13px 18px; display: flex; align-items: center; gap: 9px;
    color: #fff; font-size: 14px; font-weight: 700;
  }
  .preview-body { padding: 0 18px 4px; }
  .preview-section {
    font-size: 10px; font-weight: 700; color: #aaa;
    text-transform: uppercase; letter-spacing: 0.7px; margin: 12px 0 6px;
  }
  .preview-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 6px 0; border-bottom: 1px solid #f0f0f0; font-size: 13px; gap: 8px;
  }
  .preview-row:last-child { border-bottom: none; }
  .preview-row span  { color: #999; flex-shrink: 0; }
  .preview-row strong { color: #1b1b1b; text-align: right; max-width: 60%; word-break: break-word; }
  .preview-actions { display: flex; gap: 10px; padding: 14px 18px; border-top: 1px solid #f0f0f0; }

  .confirm-yes {
    flex: 1; background: linear-gradient(135deg, #2C963D, #1a6b28); color: #fff;
    border: none; border-radius: 20px; padding: 9px 16px; font-size: 13px; font-weight: 600;
    cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px;
    transition: opacity 0.2s, transform 0.2s; outline: none;
    box-shadow: 0 2px 8px rgba(44,150,61,0.3);
  }
  .confirm-yes:hover:not(:disabled) { transform: translateY(-1px); opacity: 0.9; }
  .confirm-yes:disabled { opacity: 0.55; cursor: not-allowed; }
  .confirm-no {
    flex: 1; background: #fff; color: #888; border: 1px solid #ddd; border-radius: 20px;
    padding: 9px 16px; font-size: 13px; font-weight: 500; cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 6px;
    transition: all 0.18s; outline: none;
  }
  .confirm-no:hover:not(:disabled) { border-color: #aaa; color: #555; }
  .confirm-no:disabled { opacity: 0.45; cursor: not-allowed; }

  /* ── Result card ── */
  .result-card {
    background: linear-gradient(135deg, #f0fdf4, #e8f5ea); border: 1.5px solid #2C963D;
    border-radius: 12px; padding: 16px; margin-top: 8px; animation: pop-in 0.25s ease;
  }
  .result-title {
    font-size: 14px; font-weight: 700; color: #1a6b28; margin-bottom: 12px;
    display: flex; align-items: center; gap: 8px;
  }
  .result-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 6px 0; border-bottom: 1px solid #d4ecd8; font-size: 13px; gap: 8px;
  }
  .result-row:last-of-type { border-bottom: none; }
  .result-row span  { color: #888; }
  .result-row strong { color: #1b1b1b; }
  .result-btn {
    margin-top: 12px; width: 100%; background: linear-gradient(135deg, #2C963D, #1a6b28);
    color: #fff; border: none; border-radius: 20px; padding: 9px 16px; font-size: 13px;
    font-weight: 600; cursor: pointer; display: flex; align-items: center;
    justify-content: center; gap: 6px; transition: opacity 0.2s, transform 0.2s;
    outline: none; box-shadow: 0 2px 8px rgba(44,150,61,0.3);
  }
  .result-btn:hover { transform: translateY(-1px); opacity: 0.9; }

  /* ── Typing ── */
  .typing {
    display: flex; align-items: center; gap: 5px; background: #fff;
    border-radius: 4px 18px 18px 18px; padding: 14px 18px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.07);
  }
  .typing span {
    display: block; width: 8px; height: 8px; border-radius: 50%; background: #2C963D;
    animation: bounce 1.2s infinite ease-in-out;
  }
  .typing span:nth-child(1){animation-delay:0s}
  .typing span:nth-child(2){animation-delay:0.2s}
  .typing span:nth-child(3){animation-delay:0.4s}
  @keyframes bounce {
    0%,80%,100%{transform:translateY(0);opacity:0.45}
    40%{transform:translateY(-7px);opacity:1}
  }

  /* ── Input area ── */
  .input-area {
    display: flex; align-items: flex-end; gap: 10px; padding: 16px 28px;
    background: #fff; border-top: 1px solid #eee; flex-shrink: 0;
  }
  .flow-hint {
    flex: 1; padding: 10px 18px; background: #f4fdf6; border: 1px dashed #c8e6cc;
    border-radius: 24px; font-size: 13px; color: #888; text-align: center;
  }
  .voice-btn {
    width: 40px; height: 40px; border-radius: 50%; border: 2px solid #d4ecd8;
    background: #fff; color: #2C963D; font-size: 16px; cursor: pointer;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    transition: all 0.2s; outline: none;
  }
  .voice-btn:hover { background: #f0faf2; border-color: #2C963D; }
  .voice-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .voice-btn.listening {
    background: #2C963D; color: #fff; border-color: #2C963D;
    animation: mic-pulse 1s infinite;
  }
  @keyframes mic-pulse {
    0%,100%{box-shadow:0 0 0 0 rgba(44,150,61,0.4)}
    50%{box-shadow:0 0 0 8px rgba(44,150,61,0)}
  }
  .input-field { flex: 1; }
  .input-field .ant-input {
    border-radius: 24px !important; padding: 10px 18px !important;
    font-size: 14px !important; border-color: #d4ecd8 !important; resize: none !important;
  }
  .input-field .ant-input:focus {
    border-color: #2C963D !important; box-shadow: 0 0 0 2px rgba(44,150,61,0.12) !important;
  }
  .send-btn {
    width: 44px; height: 44px; border-radius: 50%; border: none;
    background: linear-gradient(135deg, #2C963D, #1a6b28); color: #fff; font-size: 17px;
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; transition: opacity 0.2s, transform 0.2s; outline: none;
    box-shadow: 0 2px 10px rgba(44,150,61,0.35);
  }
  .send-btn:hover:not(:disabled) { transform: scale(1.1); }
  .send-btn:disabled { opacity: 0.35; cursor: not-allowed; transform: none; }
  .clear-btn {
    background: rgba(255,255,255,0.15); border: none; border-radius: 50%;
    width: 34px; height: 34px; display: flex; align-items: center; justify-content: center;
    cursor: pointer; color: #fff; font-size: 14px; transition: background 0.2s; outline: none;
  }
  .clear-btn:hover { background: rgba(255,255,255,0.28); }

  @media (max-width: 768px) {
    .chat-left { display: none; }
    .messages  { padding: 16px; }
    .input-area{ padding: 12px 16px; }
  }
`;

// ─── Constants ─────────────────────────────────────────────────────────────
const STATS = [
  { label: "Active Trips",     value: "8",  icon: <CarOutlined />,          color: "#2C963D" },
  { label: "Vehicles Online",  value: "14", icon: <EnvironmentOutlined />,  color: "#007AFF" },
  { label: "Deliveries Today", value: "23", icon: <CheckCircleOutlined />,  color: "#00A651" },
  { label: "Alerts",           value: "2",  icon: <AlertOutlined />,        color: "#F8B505" },
];

const CAPABILITIES = [
  { icon: <PlusCircleOutlined />, bg: "#EBF7ED", color: "#2C963D", label: "Create a consignment", sub: "AI-guided, step by step",   prompt: "__create_consignment__" },
  { icon: <CarOutlined />,        bg: "#EBF0FF", color: "#3B5BDB", label: "Create a trip",        sub: "Add a new trip",           prompt: "I want to create a trip" },
  { icon: <FileTextOutlined />,   bg: "#FFF3E0", color: "#E4A400", label: "Check trip status",    sub: "Live status & events",     prompt: "How do I check the status of a trip?" },
  { icon: <DashboardOutlined />,  bg: "#FDE8E8", color: "#C92A2A", label: "KPI Reports",          sub: "Performance metrics",      prompt: "Explain the KPI reports" },
  { icon: <EnvironmentOutlined />,bg: "#E8F5FF", color: "#1971C2", label: "Live Tracking",        sub: "Map & vehicle positions",  prompt: "How does live tracking work?" },
  { icon: <ThunderboltOutlined />,bg: "#F3F0FF", color: "#7048E8", label: "Fuel Entry",           sub: "Log fuel consumption",     prompt: "How do I log a fuel entry?" },
];

// Consignment creation steps
const CONSIGN_STEPS = [
  {
    key: "vehicle",    label: "Vehicle & Driver",
    question: "Which vehicle will handle this consignment?",
    type: "select",
    getOptions: ({ driverWithVehicle }) =>
      (driverWithVehicle || []).map((v) => ({
        label: `${v.REGNO}${v.DriverName ? ` — ${v.DriverName}` : ""}`,
        value: v.VEHID,
        display: v.REGNO,
      })),
  },
  {
    key: "tripType",   label: "Trip Type",
    question: "What type of trip is this?",
    type: "buttons",
    options: [{ label: "Local", value: 1 }, { label: "Safari", value: 2 }],
  },
  {
    key: "consignor",  label: "Consignor",
    question: "Who is the consignor (sender)?",
    type: "select",
    getOptions: ({ consignerList }) =>
      (consignerList || []).map((c) => ({ label: c.Name, value: c.ID, display: c.Name })),
  },
  {
    key: "consignee",  label: "Consignee",
    question: "Who is the consignee (receiver)?",
    type: "select",
    getOptions: ({ consigneeList }) =>
      (consigneeList || []).map((c) => ({ label: c.Name, value: c.ID, display: c.Name })),
  },
  {
    key: "transporter",label: "Transporter",
    question: "Who is the transporter / shipper?",
    type: "select",
    getOptions: ({ transporterList }) =>
      (transporterList || []).map((t) => ({ label: t.Name, value: t.ID, display: t.Name })),
  },
  {
    key: "pickupLocation", label: "Pickup Location",
    question: "What is the pickup location?",
    type: "text", placeholder: "e.g. Nairobi ICD",
  },
  {
    key: "dropoffLocation", label: "Dropoff Location",
    question: "What is the dropoff location?",
    type: "text", placeholder: "e.g. Mombasa Port",
  },
  {
    key: "pickupDateTime", label: "Pickup Date & Time",
    question: "When should goods be picked up?",
    type: "datetime",
  },
  {
    key: "dropoffDateTime", label: "Dropoff Date & Time",
    question: "When should goods be delivered?",
    type: "datetime",
  },
  {
    key: "priority",   label: "Priority",
    question: "What is the priority level?",
    type: "buttons",
    options: [{ label: "Normal", value: 1 }, { label: "High", value: 2 }],
  },
  {
    key: "remarks",    label: "Remarks",
    question: "Any remarks? (optional)",
    type: "text", placeholder: "optional notes…", optional: true,
  },
];

// ─── Helpers ───────────────────────────────────────────────────────────────
const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

const makeInitial = () => ({
  id: 1, role: "assistant", time: new Date(), result: null, flowStep: null,
  type: "normal", previewData: null, selectedValue: null,
  text: `${getGreeting()}! I'm your TrackRight AI assistant.\n\nI can guide you through creating consignments with rich dropdowns and date pickers — or answer any question about the system.\n\nWhat would you like to do?`,
});

const isCreateConsignIntent = (text) => {
  const l = text.toLowerCase();
  return (l.includes("create") || l.includes("new") || l.includes("add")) &&
    (l.includes("consign") || l.includes("shipment"));
};

const getsingleConsigneeData = async (id) => {
  try {
    const { data } = await CALL_API("GET", `entity/getbyId?id=${id}`);
    return data;
  } catch { return null; }
};

// ─── Sub-components ────────────────────────────────────────────────────────
const BotText = ({ text }) => (
  <div>
    {text.split("\n").map((line, i) => {
      if (line.startsWith("•")) return <div key={i} className="bullet-line"><span className="bullet-dot">•</span><span>{line.slice(1).trim()}</span></div>;
      if (line === "") return <div key={i} style={{ height: 6 }} />;
      return <div key={i}>{line}</div>;
    })}
  </div>
);

// Inline widget shown inside a bot message bubble
const FormWidget = ({ step, reduxData, value, onChange, onNext, active }) => {
  const [textVal, setTextVal] = useState("");

  if (!active) {
    return value ? (
      <div className="step-done"><CheckOutlined style={{ fontSize: 11 }} />{value.display || value.value}</div>
    ) : null;
  }

  if (step.type === "buttons") {
    return (
      <div className="form-widget-wrap">
        <div className="form-widget-box">
          <div className="widget-label">{step.label}</div>
          <div className="btn-grp">
            {step.options.map((opt) => (
              <button
                key={opt.value}
                className={`opt-btn${value?.value === opt.value ? " chosen" : ""}`}
                onClick={() => onChange({ value: opt.value, display: opt.label })}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button className="widget-next-btn" onClick={onNext} disabled={!value}>Next →</button>
        </div>
      </div>
    );
  }

  if (step.type === "select") {
    const options = step.getOptions ? step.getOptions(reduxData) : [];
    return (
      <div className="form-widget-wrap">
        <div className="form-widget-box">
          <div className="widget-label">{step.label}</div>
          <Select
            showSearch
            placeholder={`Search ${step.label}…`}
            style={{ width: "100%", marginBottom: 10, borderRadius: 20 }}
            value={value?.value ?? undefined}
            onChange={(val, opt) => onChange({ value: val, display: opt.label })}
            filterOption={(input, option) =>
              option?.label?.toString().toLowerCase().includes(input.toLowerCase())
            }
            options={options}
            size="middle"
          />
          <button className="widget-next-btn" onClick={onNext} disabled={!value?.value}>Next →</button>
        </div>
      </div>
    );
  }

  if (step.type === "datetime") {
    return (
      <div className="form-widget-wrap">
        <div className="form-widget-box">
          <div className="widget-label">{step.label}</div>
          <DatePicker
            showTime
            format="DD MMM YYYY, HH:mm"
            style={{ width: "100%", borderRadius: 20, marginBottom: 10 }}
            value={value?.dayjsVal || null}
            onChange={(dv) => {
              if (dv) onChange({ dayjsVal: dv, value: dv.toISOString(), display: dv.format("DD MMM YYYY, HH:mm") });
            }}
          />
          <button className="widget-next-btn" onClick={onNext} disabled={!value?.value}>Next →</button>
        </div>
      </div>
    );
  }

  if (step.type === "text") {
    return (
      <div className="form-widget-wrap">
        <div className="form-widget-box">
          <div className="widget-label">{step.label}</div>
          <Input
            placeholder={step.placeholder}
            value={textVal}
            onChange={(e) => {
              setTextVal(e.target.value);
              onChange({ value: e.target.value, display: e.target.value });
            }}
            onPressEnter={() => (textVal || step.optional) && onNext()}
            style={{ borderRadius: 20, marginBottom: 10 }}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <button className="widget-next-btn" onClick={onNext} disabled={!textVal && !step.optional}>Next →</button>
            {step.optional && (
              <button className="widget-skip-btn" onClick={() => { onChange({ value: "", display: "—" }); onNext(); }}>
                Skip
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
  return null;
};

// Preview card before final submission
const PreviewCard = ({ data, onConfirm, onCancel, loading, done }) => (
  <div className="preview-card">
    <div className="preview-header"><CheckOutlined /> Consignment Preview — Please Review</div>
    <div className="preview-body">
      <div className="preview-section">Trip Details</div>
      <div className="preview-row"><span>Vehicle</span><strong>{data.vehicle?.display || "—"}</strong></div>
      <div className="preview-row"><span>Trip Type</span><strong>{data.tripType?.value === 1 ? "Local" : "Safari"}</strong></div>
      <div className="preview-section">Parties</div>
      <div className="preview-row"><span>Consignor</span><strong>{data.consignor?.display || "—"}</strong></div>
      <div className="preview-row"><span>Consignee</span><strong>{data.consignee?.display || "—"}</strong></div>
      <div className="preview-row"><span>Transporter</span><strong>{data.transporter?.display || "—"}</strong></div>
      <div className="preview-section">Logistics</div>
      <div className="preview-row"><span>Pickup Location</span><strong>{data.pickupLocation?.display || "—"}</strong></div>
      <div className="preview-row"><span>Pickup Date & Time</span><strong>{data.pickupDateTime?.display || "—"}</strong></div>
      <div className="preview-row"><span>Dropoff Location</span><strong>{data.dropoffLocation?.display || "—"}</strong></div>
      <div className="preview-row"><span>Dropoff Date & Time</span><strong>{data.dropoffDateTime?.display || "—"}</strong></div>
      <div className="preview-row"><span>Priority</span><strong>{data.priority?.value === 2 ? "High" : "Normal"}</strong></div>
      {data.remarks?.value && (
        <div className="preview-row"><span>Remarks</span><strong>{data.remarks.display}</strong></div>
      )}
    </div>
    {!done && (
      <div className="preview-actions">
        <button className="confirm-yes" onClick={onConfirm} disabled={loading}>
          {loading ? "Creating…" : <><CheckOutlined /> Create Consignment</>}
        </button>
        <button className="confirm-no" onClick={onCancel} disabled={loading}>
          <CloseOutlined /> Cancel
        </button>
      </div>
    )}
  </div>
);

const ViewConsignmentCard = ({ onNavigate }) => (
  <div className="result-card">
    <div className="result-title">
      <CheckCircleOutlined style={{ color: "#2C963D" }} /> Consignment Created Successfully!
    </div>
    <button className="result-btn" onClick={onNavigate}>
      Show me the created consignment <ArrowRightOutlined />
    </button>
  </div>
);

// ─── Main component ────────────────────────────────────────────────────────
const AIChat = () => {
  const navigate = useNavigate();
  const userInfo        = useSelector((s) => s?.authTracking?.userInfo);
  const consignerList   = useSelector((s) => s?.authTracking?.consignerList);
  const consigneeList   = useSelector((s) => s?.authTracking?.consigneeList);
  const transporterList = useSelector((s) => s?.authTracking?.transporterList);
  const driverWithVehicle = useSelector((s) => s?.authTracking?.driverWithVehicle);
  const reduxData = { consignerList, consigneeList, transporterList, driverWithVehicle };

  const [messages,       setMessages]       = useState([makeInitial()]);
  const [history,        setHistory]        = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [input,          setInput]          = useState("");
  const [listening,      setListening]      = useState(false);
  const [consignFlow,    setConsignFlow]    = useState(null); // { step, data }
  const [widgetValue,    setWidgetValue]    = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmedIds,   setConfirmedIds]   = useState(new Set());
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const addBotMsg = (fields) =>
    setMessages((prev) => [...prev, {
      id: Date.now() + Math.random(), role: "assistant", time: new Date(),
      result: null, flowStep: null, type: "normal", previewData: null, selectedValue: null,
      ...fields,
    }]);

  const addUserMsg = (text) =>
    setMessages((prev) => [...prev, { id: Date.now(), role: "user", text, time: new Date() }]);

  // ── Consignment creation flow ──
  const startConsignFlow = () => {
    setConsignFlow({ step: 0, data: {} });
    setWidgetValue(null);
    addBotMsg({
      text: `Let's create a consignment! I'll guide you through ${CONSIGN_STEPS.length} steps using dropdowns and date pickers.\n\n${CONSIGN_STEPS[0].question}`,
      flowStep: 0,
    });
  };

  const handleWidgetNext = () => {
    if (!consignFlow) return;
    const currentStep = CONSIGN_STEPS[consignFlow.step];
    const val = widgetValue;
    if (!val?.value && !currentStep.optional) return;

    // Mark current step's message with the selected value
    setMessages((prev) => prev.map((m) =>
      m.flowStep === consignFlow.step ? { ...m, selectedValue: val } : m
    ));

    const displayText = val?.display || (currentStep.optional ? "Skipped" : "—");
    addUserMsg(displayText);

    const newData = { ...consignFlow.data, [currentStep.key]: val };
    const nextStepIdx = consignFlow.step + 1;
    setWidgetValue(null);

    if (nextStepIdx < CONSIGN_STEPS.length) {
      setConsignFlow({ step: nextStepIdx, data: newData });
      setTimeout(() => {
        addBotMsg({
          text: CONSIGN_STEPS[nextStepIdx].question,
          flowStep: nextStepIdx,
        });
      }, 250);
    } else {
      // All steps done → show preview
      setConsignFlow(null);
      setTimeout(() => {
        addBotMsg({ text: "All done! Here's a full summary — please review before creating:", type: "preview", previewData: newData });
      }, 250);
    }
  };

  const submitConsignment = (msgId) => {
    setConfirmLoading(true);
    setTimeout(() => {
      setConfirmedIds((p) => new Set(p).add(msgId));
      setConfirmLoading(false);
      addBotMsg({ text: "Consignment created successfully!", showViewBtn: true });
    }, 800);
  };

  const cancelFlow = (msgId) => {
    setConfirmedIds((p) => new Set(p).add(msgId));
    setConsignFlow(null);
    setWidgetValue(null);
    addBotMsg({ text: "Consignment creation cancelled. What else can I help with?" });
  };

  // ── Normal AI chat ──
  const sendMessage = async (userInput) => {
    const trimmed = (userInput ?? input).trim();
    if (!trimmed || loading || consignFlow) return;

    if (trimmed === "__create_consignment__" || isCreateConsignIntent(trimmed)) {
      if (trimmed !== "__create_consignment__") addUserMsg(trimmed);
      setInput("");
      startConsignFlow();
      return;
    }

    setInput("");
    addUserMsg(trimmed);
    setLoading(true);

    try {
      const res = await CALL_API("POST", "ai/chat", { message: trimmed, history });
      const payload = res?.data ?? res;
      const replyRaw = payload?.reply;
      const replyText = Array.isArray(replyRaw)
        ? replyRaw.find((b) => b.type === "text")?.text
        : replyRaw;

      addBotMsg({
        text: replyText || "Sorry, I couldn't get a response. Please try again.",
        result: payload?.result || null,
      });
      setHistory(payload?.history || []);
    } catch {
      addBotMsg({ text: "Something went wrong. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([makeInitial()]);
    setHistory([]);
    setLoading(false);
    setConsignFlow(null);
    setWidgetValue(null);
  };

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR(); r.lang = "en-US"; r.interimResults = false;
    setListening(true); r.start();
    r.onresult = (e) => { setInput(e.results[0][0].transcript); setListening(false); };
    r.onerror = r.onend = () => setListening(false);
  };

  const fmt  = (d) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const voiceOk = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const currentStep = consignFlow ? CONSIGN_STEPS[consignFlow.step] : null;

  return (
    <PageWrap>
      {/* ── Header ── */}
      <div className="chat-header">
        <div className="chat-header-left">
          <div className="chat-avatar"><RobotOutlined /></div>
          <div>
            <div className="chat-header-title">TrackRight AI Assistant</div>
            <div className="chat-header-sub">
              <span className="live-dot" />
              {consignFlow
                ? `Step ${consignFlow.step + 1} of ${CONSIGN_STEPS.length} — ${currentStep?.label}`
                : loading ? "Thinking…" : "Always on · Ask anything or create a consignment"}
            </div>
          </div>
        </div>
        {/* Progress bar inside header during flow */}
        {consignFlow && (
          <div className="flow-progress-bar" style={{ flex: 1, margin: "0 20px" }}>
            {CONSIGN_STEPS.map((_, i) => (
              <div key={i} className={`flow-progress-seg${i <= consignFlow.step ? " done" : ""}`} />
            ))}
          </div>
        )}
        <Tooltip title="Clear conversation">
          <button className="clear-btn" onClick={clearChat}><DeleteOutlined /></button>
        </Tooltip>
      </div>

      <div className="chat-body">
        {/* ── Left sidebar ── */}
        <div className="chat-left">
          <div>
            <div className="left-section-title">Fleet Overview</div>
            <div className="stats-grid">
              {STATS.map((s) => (
                <div key={s.label} className="stat-card">
                  <div className="stat-icon" style={{ color: s.color }}>{s.icon}</div>
                  <div className="stat-val"  style={{ color: s.color }}>{s.value}</div>
                  <div className="stat-lbl">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="left-section-title">What I can do</div>
            {CAPABILITIES.map((c) => (
              <div key={c.label} className="cap-card" onClick={() => sendMessage(c.prompt)}>
                <div className="cap-icon" style={{ background: c.bg, color: c.color }}>{c.icon}</div>
                <div>
                  <div className="cap-label">{c.label}</div>
                  <div className="cap-sub">{c.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Chat area ── */}
        <div className="chat-right">
          <div className="messages">
            {messages.map((msg) => {
              const isBot = msg.role === "assistant";
              const isActiveWidget = consignFlow !== null && consignFlow.step === msg.flowStep;

              return (
                <div key={msg.id}>
                  <div className={`msg-row ${isBot ? "bot" : "user"}`}>
                    {isBot && <div className="msg-avatar">✦</div>}
                    <div className="msg-wrap">
                      <div className={`msg-bubble ${isBot ? "bot" : "user"}`}>
                        <BotText text={msg.text} />

                        {/* Form widget — only for flow step messages */}
                        {isBot && msg.flowStep !== null && (
                          <FormWidget
                            key={`widget-${msg.flowStep}-${isActiveWidget}`}
                            step={CONSIGN_STEPS[msg.flowStep]}
                            reduxData={reduxData}
                            value={isActiveWidget ? widgetValue : msg.selectedValue}
                            onChange={isActiveWidget ? setWidgetValue : () => {}}
                            onNext={isActiveWidget ? handleWidgetNext : () => {}}
                            active={isActiveWidget}
                          />
                        )}
                      </div>
                      <div className="msg-time">{fmt(msg.time)}</div>
                    </div>
                  </div>

                  {/* Preview card */}
                  {isBot && msg.type === "preview" && msg.previewData && (
                    <div style={{ paddingLeft: 42, marginTop: 8 }}>
                      <PreviewCard
                        data={msg.previewData}
                        loading={confirmLoading && !confirmedIds.has(msg.id)}
                        done={confirmedIds.has(msg.id)}
                        onConfirm={() => submitConsignment(msg.id, msg.previewData)}
                        onCancel={() => cancelFlow(msg.id)}
                      />
                    </div>
                  )}

                  {/* View consignment card */}
                  {isBot && msg.showViewBtn && (
                    <div style={{ paddingLeft: 42, marginTop: 8 }}>
                      <ViewConsignmentCard onNavigate={() => navigate("/Consignment1")} />
                    </div>
                  )}
                </div>
              );
            })}

            {loading && (
              <div className="msg-row bot">
                <div className="msg-avatar">✦</div>
                <div className="typing"><span /><span /><span /></div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <div className="input-area">
            {voiceOk && !consignFlow && (
              <Tooltip title={listening ? "Listening…" : "Voice input"}>
                <button className={`voice-btn${listening ? " listening" : ""}`} onClick={startVoice} disabled={loading}>
                  <AudioOutlined />
                </button>
              </Tooltip>
            )}
            {consignFlow ? (
              <div className="flow-hint">
                Use the fields above to continue — Step {consignFlow.step + 1} of {CONSIGN_STEPS.length}
              </div>
            ) : (
              <div className="input-field">
                <Input.TextArea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder="Ask anything or say 'create a consignment'…"
                  autoSize={{ minRows: 1, maxRows: 5 }}
                  disabled={loading}
                />
              </div>
            )}
            {!consignFlow && (
              <button className="send-btn" onClick={() => sendMessage()} disabled={!input.trim() || loading}>
                <SendOutlined />
              </button>
            )}
          </div>
        </div>
      </div>
    </PageWrap>
  );
};

export default AIChat;
